
import { useState, useEffect } from "react";
import { appStoreCatalog, macOSCatalog } from "@/data/mockApps";
import { Application } from "@/types/application";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

// Map database row to Application type
const mapDbToApplication = (row: any): Application => ({
  id: row.id,
  name: row.name,
  icon: row.icon,
  description: row.description,
  version: row.version,
  size: row.size,
  releaseDate: row.release_date,
  source: row.source as Application['source'],
  platform: row.platform as Application['platform'],
  bundleId: row.bundle_id,
  categories: row.categories,
  customers: row.customers || [],
  status: row.status as Application['status'],
  abmConnectionId: row.abm_connection_id,
  appStoreId: row.app_store_id,
  appStoreUrl: row.app_store_url,
  licenseTotal: row.license_total,
  licenseUsed: row.license_used,
  countryCode: row.country_code,
  price: row.price,
  minimumOsVersion: row.minimum_os_version,
  developer: row.developer,
  rating: row.rating,
});

// Map Application type to database insert format
const mapApplicationToDb = (app: Omit<Application, "id">) => ({
  name: app.name,
  icon: app.icon,
  description: app.description || null,
  version: app.version,
  size: app.size,
  release_date: app.releaseDate,
  source: app.source,
  platform: app.platform,
  bundle_id: app.bundleId || null,
  categories: app.categories || null,
  customers: app.customers || null,
  status: app.status,
  abm_connection_id: app.abmConnectionId || null,
  app_store_id: app.appStoreId || null,
  app_store_url: app.appStoreUrl || null,
  license_total: app.licenseTotal || null,
  license_used: app.licenseUsed || null,
  country_code: app.countryCode || null,
  price: app.price || null,
  minimum_os_version: app.minimumOsVersion || null,
  developer: app.developer || null,
  rating: app.rating || null,
});

export const useApplications = (customerId?: string) => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('applications')
        .select('*')
        .order('created_at', { ascending: false });
      
      // Filter by customer if provided
      if (customerId) {
        query = query.contains('customers', [customerId]);
      }
      
      const { data, error: fetchError } = await query;
      
      if (fetchError) throw fetchError;
      
      const mappedApps = (data || []).map(mapDbToApplication);
      setApplications(mappedApps);
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to fetch applications"));
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, [customerId]);

  const addApplication = async (app: Omit<Application, "id">): Promise<Application> => {
    try {
      const dbData = mapApplicationToDb(app);
      
      const { data, error: insertError } = await supabase
        .from('applications')
        .insert([dbData])
        .select()
        .single();
      
      if (insertError) throw insertError;
      
      const newApp = mapDbToApplication(data);
      
      // Update local state
      setApplications(prev => [newApp, ...prev]);
      
      toast.success("Application added successfully");
      return newApp;
    } catch (error) {
      console.error("Failed to add application:", error);
      toast.error("Failed to add application");
      throw error;
    }
  };

  const updateApplication = async (updatedApp: Application): Promise<Application> => {
    try {
      const { id, ...appWithoutId } = updatedApp;
      const dbData = mapApplicationToDb(appWithoutId);
      
      const { data, error: updateError } = await supabase
        .from('applications')
        .update(dbData)
        .eq('id', id)
        .select()
        .single();
      
      if (updateError) throw updateError;
      
      const mapped = mapDbToApplication(data);
      
      setApplications(prev => 
        prev.map(app => app.id === id ? mapped : app)
      );
      
      toast.success("Application updated successfully");
      return mapped;
    } catch (error) {
      console.error("Failed to update application:", error);
      toast.error("Failed to update application");
      throw error;
    }
  };

  const removeApplication = async (appId: string): Promise<void> => {
    try {
      const { error: deleteError } = await supabase
        .from('applications')
        .delete()
        .eq('id', appId);
      
      if (deleteError) throw deleteError;
      
      setApplications(prev => prev.filter(app => app.id !== appId));
      toast.success("Application removed successfully");
    } catch (error) {
      console.error("Failed to remove application:", error);
      toast.error("Failed to remove application");
      throw error;
    }
  };

  const getAppStoreCatalog = () => appStoreCatalog;
  const getMacOSCatalog = () => macOSCatalog;

  return {
    applications,
    loading,
    error,
    addApplication,
    updateApplication,
    removeApplication,
    getAppStoreCatalog,
    getMacOSCatalog,
    refetch: fetchApplications,
  };
};
